import { isImproveInProgress } from './feedAssetGeneration';
import type { LongFormFeedAsset, LongFormFeedGroup } from './types';

/**
 * One part of a long-form document: what the card renders as a row. An improve keeps the text it
 * started from, so a part may carry an original alongside the asset that replaced it.
 */
export interface LongFormAssetPart {
  asset: LongFormFeedAsset;
  original?: LongFormFeedAsset;
  /**
   * The improve has not written this part yet, so the text it is being written from stands in for
   * it — as a place in the document rather than as something to read.
   */
  awaitingImprove?: boolean;
}

/**
 * The parts of a long-form group, in the order its assets arrived.
 *
 * A group returns originals as assets of their own, sharing a `position` with the asset that
 * improved them. An original is never a part: it is shown inside the one that names it. An asset
 * with no original is ordinary — a part the user added, or one from a generation that had nothing
 * to improve.
 *
 * While an improve runs, the group holds the text being improved before it holds anything
 * improved, and those assets are finished rows with real ids — nothing about one of them says it
 * is standing in for something. What says it is the run: in a group the Content Improver is still
 * generating into, an asset that names no original is the text a part is being written from, so it
 * holds that part's place until the part arrives.
 */
export const longFormAssetParts = (
  group: Pick<LongFormFeedGroup, 'assets' | 'sourceType' | 'status'>
): LongFormAssetPart[] => {
  const { assets } = group;
  const byId = new Map(assets.map((asset) => [String(asset.id), asset]));
  const improvedByOriginalId = new Map(
    assets
      .filter((asset) => asset.originalLongFormAssetId)
      .map((asset) => [String(asset.originalLongFormAssetId), asset])
  );
  const improving = isImproveInProgress(group);

  return assets.flatMap((asset): LongFormAssetPart[] => {
    if (asset.originalLongFormAssetId) {
      return [{ asset, original: byId.get(String(asset.originalLongFormAssetId)) }];
    }

    if (improvedByOriginalId.has(String(asset.id))) {
      return [];
    }

    return [improving ? { asset, awaitingImprove: true } : { asset, original: undefined }];
  });
};
